import React from 'react';
import { SystemStore, AuxillarySystemStore } from '../store/system-stores';
import { GpuAvailability, bytesToSize } from '../util';
import { systemApi } from '../api-layer';
import Graph from 'react-chartist';
import { LoadingScreen, Gauge, Bar } from '../component';
import {
  panel,
  FlexRow,
  FlexCol,
  createDivComponent,
  glyphicon,
  FontAwesome,
} from '../component/ui';
import _ from 'lodash';
import changeCase from 'change-case';
import { inject, observer } from 'mobx-react';
import { Stores } from '../store';

const { Panel, Fixed, FixedCenter, Flex, FlexFixed, HeaderCenter } = panel;

const SystemFooter = createDivComponent('system-footer');

let graphOptions = {
  axisX: {
    showLabel: false,
    showGrid: false,
  },
  axisY: {
    showLabel: true,
    showGrid: true,
  },
  showArea: true,
  showPoint: false,
  high: 100,
  low: 0,
  height: '150px',
  width: '100%',
  chartPadding: {
    top: 20,
    bottom: -20,
    left: 0,
    right: 20,
  },
};

function StatHeader(props: {
  headLeft?: React.ReactChild;
  headRight?: React.ReactChild;
}) {
  const { headLeft, headRight } = props;
  return (
    <div className="clearfix stat-header">
      <div className="left" style={{ float: 'left' }}>
        {headLeft}
      </div>
      <div className="right" style={{ float: 'right' }}>
        {headRight}
      </div>
    </div>
  );
}

function StatContainer(props: { children?: any }) {
  const { children } = props;
  return <div className="stat-container">{children}</div>;
}

function StatItem(props: { head: string; isFooter?: boolean; children?: any }) {
  const { head, isFooter, children } = props;
  return (
    <div className={isFooter ? 'foot-stat-item' : 'stat-item'}>
      <span className="stat-item-head-text">{head}</span>
      <br />
      {children}
    </div>
  );
}

type StatsOwnProps = {};
type StatsStoreProps = { systemStore: SystemStore };
type StatsProps = StatsOwnProps & StatsStoreProps;

class StatsComp extends React.Component<StatsProps, {}> {
  render() {
    if (
      !(this.props.systemStore.stats && this.props.systemStore.stats.cpuUsage)
    ) {
      return <p>Loading stats...</p>;
    }

    const { systemStore } = this.props;
    const { stats } = systemStore;

    let cpuSeriesNonReduced = systemStore.stats.cpuUsage.map((_, i) =>
      systemStore.statStack.map(stats => stats.cpuUsage[i]),
    );

    let cpuSeries: number[] = [];

    if (cpuSeriesNonReduced.length > 0) {
      const cpuSeriesReversed = cpuSeriesNonReduced.map(cpu =>
        [...cpu].reverse(),
      );

      const processors = cpuSeriesReversed.length;
      cpuSeries = cpuSeriesReversed
        .reduce((cpu1, cpu2) => cpu1.map((v, i) => v + cpu2[i]))
        .map(cpu => cpu / processors);
    }

    let ramSeries = this.props.systemStore.statStack.map(stats => {
      return stats.usedMemory / stats.totalMemory * 100;
    });

    ramSeries = ramSeries.reverse();

    // Fill series with leading zeroes for better presentation
    while (cpuSeries.length < systemStore.maxLengthOfStack)
      cpuSeries.unshift(0);
    while (ramSeries.length < systemStore.maxLengthOfStack)
      ramSeries.unshift(0);

    return (
      <div>
        <Panel style={{ marginBottom: 20 }}>
          <div className="header">
            <StatHeader headLeft={'CPU'} />
          </div>
          <div className="body">
            <StatContainer>
              <StatItem head="Usage">
                {cpuSeries[cpuSeries.length - 1] &&
                  cpuSeries[cpuSeries.length - 1].toFixed(0)}%
              </StatItem>
              <StatItem head="Processes">{stats.runningProcesses}</StatItem>
            </StatContainer>
            <Graph
              data={{
                series: [cpuSeries],
              }}
              options={graphOptions}
              type={'Line'}
            />
          </div>
        </Panel>
        <Panel>
          <div className="header">
            <StatHeader
              headLeft="RAM"
              headRight={bytesToSize(stats.totalMemory)}
            />
          </div>
          <div className="body">
            <StatContainer>
              <StatItem head="Usage">
                {ramSeries[ramSeries.length - 1] &&
                  ramSeries[ramSeries.length - 1].toFixed(0)}%
              </StatItem>
              <StatItem head="In Use">{bytesToSize(stats.usedMemory)}</StatItem>
              <StatItem head="Free">
                {bytesToSize(stats.availableMemory)}
              </StatItem>
            </StatContainer>
            <Graph
              data={{
                series: [ramSeries],
              }}
              options={graphOptions}
              type={'Line'}
            />
          </div>
        </Panel>
      </div>
    );
  }
}

export const Stats = inject<Stores, StatsOwnProps, StatsStoreProps, {}>(
  ({ systemStore }) => ({
    systemStore,
  }),
)(observer(StatsComp)) as React.ComponentClass<StatsOwnProps>;

interface SystemPanelProps extends React.HTMLProps<HTMLDivElement> {
  title: string;
  image: React.ReactChild;
  emptyBody?: boolean;
  flexGrow?: number;
  flexShrink?: number;
}

function SystemPanel(props: SystemPanelProps) {
  const {
    title,
    image,
    flexGrow,
    flexShrink,
    children,
    emptyBody,
    ...rest
  } = props;

  return (
    <Panel style={{ flexGrow, flexShrink }} className="flex-child" {...rest}>
      <HeaderCenter>
        {title} <br /> <br />
        {image}
      </HeaderCenter>
      {emptyBody
        ? [
            <Flex key="flex" />,
            <FixedCenter key="center">{children}</FixedCenter>,
          ]
        : children}
    </Panel>
  );
}

function Faded(props: { children?: any }) {
  return <span className="faded">{props.children}</span>;
}

let updateInterval;

function getNetworkDevices(count: number): NetworkDeviceInfo[] {
  let devices: NetworkDeviceInfo[] = [];
  for (let i = 0; i < count; i++) {
    devices.push({
      Ip: '0.0.0.0',
      Name: 'Unknown',
      MacAddress: '5404A6419018',
    });
  }
  return devices;
}

let testDevices = getNetworkDevices(20);

function getDeviceGlyph(name: string) {
  let uName = name.toUpperCase();
  if (uName.indexOf('IPHONE') !== -1 || uName.indexOf('ANDROID') !== -1) {
    return 'phone';
  }
  if (uName.indexOf('DESKTOP') !== -1 || uName.indexOf('MAC') !== -1) {
    return 'user';
  }
  return 'map-marker';
}

interface CollectionTableProps extends React.HTMLAttributes<HTMLTableElement> {
  collection: { [key: string]: any }[];
}

function CollectionTable(props: CollectionTableProps) {
  const { collection, ...other } = props;
  return (
    <table {...other}>
      <thead>
        <tr>
          {_.map(collection[0], (v, k) => {
            return <th key={k}>{k}</th>;
          })}
        </tr>
      </thead>
      <tbody>
        {collection.map((obj, i) => {
          return (
            <tr key={i}>
              {_.map(obj, (v, k) => {
                return <td key={k}>{v}</td>;
              })}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

interface PopoutRowProps extends React.HTMLAttributes<HTMLDivElement> {}
class PopoutRow extends React.Component<PopoutRowProps, {}> {
  barElement: HTMLDivElement;
  render() {
    return (
      <div className="popout-row" ref={ref => (this.barElement = ref)}>
        {React.Children.map(
          this.props.children,
          (child: React.ReactElement<any>) => {
            return React.cloneElement(child, {
              externalParent: this.barElement,
            });
          },
        )}
        {/*this.props.popouts.map(popout => {
                return <Popout label={popout.label} externalParent={this.barElement}>
                    {React.cloneElement(popout.content)}
                </Popout>
            })*/}
      </div>
    );
  }
}

interface PopoutProps extends React.HTMLAttributes<HTMLDivElement> {
  label?: string;
  externalParent?: HTMLDivElement;
  anchor?: string;
}
interface PopoutState {
  open?: boolean;
}
class Popout extends React.Component<PopoutProps, PopoutState> {
  arrowElement: HTMLDivElement;
  popoutElement: HTMLDivElement;
  constructor(props, context) {
    super(props, context);
    this.state = {
      open: false,
    };
  }
  toggle = () => {
    if (this.state.open) {
      document.removeEventListener('click', this.clickHandler);
    } else {
      document.addEventListener('click', this.clickHandler);
    }
    this.setState({ open: !this.state.open });
  };
  componentWillUnmount() {
    document.removeEventListener('click', this.clickHandler);
  }
  showPopout() {
    const { externalParent, anchor } = this.props;
    const parent = externalParent || this.arrowElement;
    const popout = this.popoutElement;
    if (parent && popout) {
      const $popout = $(popout);
      let top = 0;
      let left = 0;
      const documentHeight = $(document).height();
      const documentWidth = $(document).width();
      $popout.css({
        top: 10,
        left: 10,
        display: 'block',
        visibility: 'hidden',
      });
      //popout.style.top = $(document).height()/2 + "px"
      //popout.style.left = $(document).width()/2 + "px"
      //popout.style.display = "block"
      //popout.style.visibility = "hidden"
      const [popoutWidth, popoutHeight] = [
        popout.clientWidth,
        popout.clientHeight,
      ];
      const [parentTop, parentLeft, parentWidth, parentHeight] = [
        parent.offsetTop,
        parent.offsetLeft,
        parent.offsetWidth,
        parent.offsetHeight,
      ];
      if (
        parentTop + popoutHeight > documentHeight &&
        popoutHeight < parentTop
      ) {
        $popout.css({
          top: parentTop - popoutHeight,
        });
      } else {
        $popout.css({
          top: parentTop + parentHeight,
        });
      }
      if (parentLeft - popoutWidth / 2 < 0) {
        $popout.css({ left: 0 });
      } else if (parentLeft + popoutWidth / 2 > documentWidth) {
        $popout.css({ left: documentWidth - popoutWidth });
      } else {
        $popout.css({ left: parentLeft + parentWidth / 2 - popoutWidth / 2 });
      }
      $popout.css({ visibility: 'visible' });
    }
  }
  hidePopout() {
    const popout = this.popoutElement;
    if (popout) {
      const $popout = $(popout);
      $popout.css({ display: 'none' });
    }
  }
  componentDidUpdate(prevProps, prevState: PopoutState) {
    if (this.state.open) {
      this.showPopout();
    } else if (!this.state.open && prevState.open) {
      this.hidePopout();
    }
  }
  table() {
    const style: React.CSSProperties = {
      position: 'absolute',
      display: 'none',
    };
    return (
      <div
        className="popout-content-container"
        style={style}
        ref={ref => (this.popoutElement = ref)}
      >
        {this.props.children}
      </div>
    );
  }
  clickHandler = (e: MouseEvent) => {
    if (
      this.popoutElement &&
      this.state.open &&
      !this.popoutElement.contains(e.target as any)
    ) {
      this.toggle();
    }
  };
  arrow() {
    const { open } = this.state;
    const { label } = this.props;
    return (
      <div
        onClick={this.toggle}
        className="popout-arrow"
        ref={ref => (this.arrowElement = ref)}
      >
        {!!label && label + ' '}
        {glyphicon(open ? 'chevron-up' : 'chevron-down')}
      </div>
    );
  }
  render() {
    const { label, externalParent, anchor, ...rest } = this.props;

    return (
      <div className="popout-container" {...rest}>
        {this.arrow()}
        {this.table()}
      </div>
    );
  }
}

function isTrue(condition: boolean, label: string) {
  return condition ? label : `Not ${label}`;
}

const panels = {
  OS(os: OSInfo) {
    return (
      <SystemPanel
        emptyBody
        flexGrow={1}
        style={{ width: '50%' }}
        title="operating system"
        image={<img src={require('img/icon/pc.svg')} width="62" height="49" />}
      >
        {os.name} {os.architecture} <br />
        <Faded>
          Version {os.version} <br />
          Build {os.build}
        </Faded>
      </SystemPanel>
    );
  },
  motherboard(stats: SystemInfo) {
    return (
      <SystemPanel
        emptyBody
        flexGrow={1}
        style={{ width: '50%' }}
        title="motherboard"
        image={
          <img src={require('icon/motherboard.svg')} width="45" height="40" />
        }
      >
        {stats.motherBoard} <br />
        <Faded>
          BIOS from {stats.biosInfo ? stats.biosInfo.biosManufacturer : ''}{' '}
          <br />
          {stats.biosInfo ? stats.biosInfo.biosCaption : ''}
        </Faded>
      </SystemPanel>
    );
  },
  network(network: NetworkInfo, stats: SystemInfo) {
    return (
      <SystemPanel
        flexGrow={1}
        title="network"
        image={<img src={require('icon/network.svg')} width="52" height="40" />}
      >
        <FixedCenter>Devices</FixedCenter>
        <FlexFixed>
          {network.networkDevices
            .filter(d => d.MacAddress !== network.macAddress)
            .map(device => {
              return (
                <FixedCenter key={device.Name}>
                  {glyphicon(getDeviceGlyph(device.Name))} <br />
                  {device.Name} <br />
                  <Faded>{device.Ip}</Faded>
                </FixedCenter>
              );
            })}
        </FlexFixed>
        <SystemFooter>
          <StatItem isFooter head="public ip">
            <span className="hover-container">
              <span className="hover">{network.publicIp}</span>
              <span className="unhovered highlighted">Hidden</span>
            </span>
          </StatItem>
          <StatItem isFooter head="internal ip">
            {network.internalIp}
          </StatItem>
          <StatItem isFooter head="mac address">
            {network.macAddress}
          </StatItem>
          <StatItem isFooter head="data sent">
            {bytesToSize(stats.networkInfo.totalBytesSent)}
          </StatItem>
          <StatItem isFooter head="data received">
            {bytesToSize(stats.networkInfo.totalBytesReceived)}
          </StatItem>
        </SystemFooter>
      </SystemPanel>
    );
  },
  cpu(cpu: CpuInfo, stats: SystemInfo) {
    const { cpuName, cores, threads, speedMhz, ...cpuOther } = cpu;
    return (
      <SystemPanel
        flexGrow={1}
        title="CPU"
        image={<img src={require('icon/cpu.svg')} width="40" height="40" />}
      >
        <FixedCenter>{cpu.cpuName}</FixedCenter>
        <Flex>
          <div className="gauge-box">
            <Gauge
              title="Temperature"
              label={stats.cpuTemps == null ? '' : '° C'}
              value={
                stats.cpuTemps == null
                  ? 'N/A'
                  : stats.cpuTemps[stats.cpuTemps.length - 1].toFixed(0)
              }
              min={0}
              max={100}
            />
          </div>
        </Flex>
        <Flex>
          <Popout
            style={{ textAlign: 'center' }}
            label="details"
            anchor="right"
          >
            <table className="cpu-table">
              <tbody>
                {_(cpuOther)
                  .map((v, k) => {
                    return (
                      <tr key={k}>
                        <td>{changeCase.sentenceCase(k)}</td>
                        <td>{v}</td>
                      </tr>
                    );
                  })
                  .value()}
              </tbody>
            </table>
          </Popout>
        </Flex>
        <SystemFooter>
          <StatItem isFooter head="cores">
            {cores}
          </StatItem>
          <StatItem isFooter head="threads">
            {threads}
          </StatItem>
          <StatItem isFooter head="clock speed">
            {(speedMhz / 1000).toFixed(1)} GHz
          </StatItem>
        </SystemFooter>
      </SystemPanel>
    );
  },
  gpu(gpu: GpusInfo) {
    return (
      <SystemPanel
        title="video cards"
        flexGrow={1}
        image={<img src={require('icon/gpu.svg')} width="54" height="40" />}
      >
        <FlexFixed>
          {gpu.gpus.map(info => {
            let temp = info.temperature;
            return (
              <FixedCenter
                style={{ flexGrow: 1, flexBasis: 0 }}
                key={info.name}
              >
                {info.name} <br />
                <Faded>Driver Version: {info.driverVersion}</Faded> <br />
                <span
                  className={
                    'label label-' +
                    (info.status == 'OK' ? 'success' : 'danger')
                  }
                >
                  {info.status}
                </span>
                &nbsp;
                <span className="label label-success">
                  {GpuAvailability[info.availability]}
                </span>
                <div className="gauge-box">
                  <Gauge
                    title="Temperature"
                    label={temp != -1 ? '° C' : ''}
                    value={temp != -1 ? temp.toFixed(0) : 'N/A'}
                    min={0}
                    max={100}
                  />
                </div>
              </FixedCenter>
            );
          })}
        </FlexFixed>
        <Flex />
      </SystemPanel>
    );
  },
  display(stats: SystemInfo) {
    const { displays } = stats;
    return (
      <SystemPanel flexGrow={1} title="displays" image={<img src="" />}>
        <FlexFixed>
          {!displays.length ? <p>N/A</p> : ''}
          {displays.map(display => {
            const {
              friendlyName,
              currentResolution,
              deviceName,
              primary,
              attached,
              removable,
              supportedResolutions,
              remote,
              ...otherInfo
            } = display;
            const processedResolutions = _.mapValues(
              supportedResolutions,
              v => {
                if (v.length == 1) {
                  return v[0];
                }
                let reduced = v.reduce((prev, curr) => {
                  let { frequency, ...others } = prev;
                  return {
                    frequency: _.flatten([frequency]).concat(curr.frequency),
                    ...others,
                  };
                });
                return {
                  ...reduced,
                  frequency: _.uniq(reduced.frequency as number[]).join(', '),
                };
              },
            );
            return (
              <FixedCenter
                style={{ flexGrow: 1, flexBasis: 0 }}
                key={display.deviceName}
              >
                <p>
                  <FontAwesome fa="television" size="2x" />
                </p>
                <p>
                  {friendlyName} <br />
                  {currentResolution.width}x{currentResolution.height}
                </p>
                <Popout anchor="right">
                  as {deviceName} <br />
                  {isTrue(attached, 'Attached')}, &nbsp;
                  {isTrue(removable, 'Removable')}, &nbsp;
                  {primary ? 'Primary' : 'Secondary'}, &nbsp;
                  {remote ? 'Remote' : 'Local'}
                  <br />
                  <strong>Supported Resolutions:</strong> <br />
                  <table className="cpu-table">
                    <thead>
                      <tr>
                        <th>Resolution</th>
                        <th>BPP</th>
                        <th>Frequencies (Hz)</th>
                        <th>Orientation</th>
                      </tr>
                    </thead>
                    <tbody>
                      {_.values(processedResolutions).map(res => {
                        return (
                          <tr key={`${res.width}x${res.height}`}>
                            <td>
                              {res.width}x{res.height}
                            </td>
                            <td>{res.bitsPerPixel}</td>
                            <td>{res.frequency}</td>
                            <td>{res.orientation}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </Popout>
              </FixedCenter>
            );
          })}
        </FlexFixed>
      </SystemPanel>
    );
  },
  drive(stats: SystemInfo) {
    return (
      <SystemPanel
        flexGrow={1}
        title="drives"
        image={<img src={require('icon/drive.svg')} width="30" height="40" />}
      >
        <Fixed>
          {stats.drives.map(drive => {
            return (
              <div className="drive-item" key={drive.volumeLabel}>
                <div key={drive.rootDirectory} className="graph-item">
                  <div className="graph-label">
                    {drive.volumeLabel.length > 0
                      ? drive.volumeLabel
                      : 'No label'},&nbsp;
                    {drive.rootDirectory} <br />
                    <Faded>{bytesToSize(drive.totalSize)}</Faded>
                  </div>
                  <div className="graph-bar">
                    <Bar
                      value={100 - drive.freeSpace / drive.totalSize * 100}
                      style={{ width: '100%' }}
                      color={true}
                    />
                    <Faded>{bytesToSize(drive.freeSpace)} Free</Faded>
                  </div>
                </div>
                <div className="drive-popout-row">
                  <PopoutRow>
                    <Popout label="smart" anchor="right">
                      <CollectionTable
                        className="smart-table"
                        collection={drive.smartData.map(d =>
                          _.omit(d, 'RawData'),
                        )}
                      />
                    </Popout>
                    <Popout label="partitions" anchor="right">
                      {drive.partitions.map(p => {
                        let { name, ...therest } = p;
                        return (
                          <div className="partition-information" key={name}>
                            <p>
                              <strong>{name}</strong>
                            </p>
                            <table>
                              <tbody>
                                {_.map(therest as any, (v, k: string) => {
                                  return (
                                    <tr key={k}>
                                      <td>{changeCase.sentenceCase(k)}</td>
                                      <td>{v}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        );
                      })}
                    </Popout>
                  </PopoutRow>

                  {/*<PopoutRow popouts={[
                                {label: "smart", content: <CollectionTable collection={drive.SmartData} />},
                                {label: "partitions", content: <div>other info</div>}
                            ]}/> */}
                </div>
                {/*<Popout label="SMART">
                            <CollectionTable collection={drive.SmartData} />
                        </Popout> */}
              </div>
            );
          })}
        </Fixed>
        <Flex />
      </SystemPanel>
    );
  },
};

type SystemPageOwnProps = { path: string };
type SystemPageStoreProps = {
  systemStore: SystemStore;
  auxillarySystemStore: AuxillarySystemStore;
};
type SystemPageProps = SystemPageOwnProps & SystemPageStoreProps;

class SystemPageComp extends React.Component<SystemPageProps, {}> {
  componentDidMount() {
    updateInterval = setInterval(() => {
      systemApi.getStats();
      systemApi.getAuxillaryStats();
    }, 10000);
    systemApi.getStats();
    systemApi.getAuxillaryStats();
  }
  componentWillUnmount() {
    clearInterval(updateInterval);
  }
  render() {
    const {
      auxillarySystemStore: { os, cpu, network, gpu },
      systemStore: { stats },
    } = this.props;

    if (!(os && cpu && network && gpu && stats)) {
      let percent =
        ([os, cpu, network, gpu, stats].map(
          e => (e ? 1 : 0),
        ) as number[]).reduce((a, b) => a + b) * 20;

      return (
        <LoadingScreen percentage={percent}>
          Loading system information <br />
          {_.map(_.omit(this.state, 'statStack') as any, (v, k) => {
            return k + ' ';
          })}
        </LoadingScreen>
      );
    }

    return (
      <div className="system-page">
        <FlexCol>
          <FlexRow>
            <FlexCol style={{ width: '66%' }}>
              <FlexRow>
                {panels.OS(os)}
                {panels.motherboard(stats)}
              </FlexRow>
              <FlexRow>{panels.network(network, stats)}</FlexRow>
            </FlexCol>
            {panels.cpu(cpu, stats)}
          </FlexRow>
          <FlexRow>
            <FlexCol style={{ width: '66%' }}>
              <FlexRow>{panels.gpu(gpu)}</FlexRow>
              <FlexRow>{panels.display(stats)}</FlexRow>
            </FlexCol>
            {panels.drive(stats)}
          </FlexRow>
        </FlexCol>
      </div>
    );
  }
}

export const SystemPage = inject<
  Stores,
  SystemPageOwnProps,
  SystemPageStoreProps,
  {}
>(({ systemStore, auxillarySystemStore }) => ({
  systemStore,
  auxillarySystemStore,
}))(observer(SystemPageComp)) as React.ComponentClass<SystemPageOwnProps>;
